// Canales de venta a lo largo del histórico y el embudo de estados de Hostify.

import { useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import type { HistoricoReservasResult } from '@/lib/types';
import { Tarjeta, fmtMoney, fmtNum, fmtPct, useTemaGraficas } from './comun';

const MAX_CANALES_GRAFICA = 6;

const ESTADO_LABEL: Record<string, string> = {
  accepted: 'Confirmada',
  cancelled: 'Cancelada',
  inquiry: 'Consulta',
  expired: 'Caducada',
  incomplete: 'Incompleta',
  denied: 'Rechazada',
  no_show: 'No presentado',
  not_possible: 'No posible',
  payment_failed: 'Pago fallido',
  pending: 'Pendiente',
  preapproved: 'Preaprobada',
  timedout: 'Sin respuesta',
  voided: 'Anulada',
  new: 'Nueva',
};

export default function SeccionCanales({ data }: { data: HistoricoReservasResult }) {
  const tema = useTemaGraficas();

  // Los canales grandes con su propia barra; el resto, agrupados en «Otros»
  // para que la leyenda no se llene de HomeAway_DE con tres reservas.
  const { series, grafica } = useMemo(() => {
    const principales = data.canales.slice(0, MAX_CANALES_GRAFICA).map((c) => c.canal);
    const hayOtros = data.canales.length > principales.length;
    const filas = data.canalesPorAnio.map((a) => {
      const fila: Record<string, number | string> = { anio: String(a.anio) };
      let otros = 0;
      for (const [canal, n] of Object.entries(a.valores)) {
        if (principales.includes(canal)) fila[canal] = n;
        else otros += n;
      }
      if (hayOtros) fila['Otros'] = otros;
      return fila;
    });
    return { series: hayOtros ? [...principales, 'Otros'] : principales, grafica: filas };
  }, [data]);

  const totalDocs = data.rango.documentos;

  return (
    <>
      <Tarjeta
        titulo="Canales"
        info="Reservas confirmadas por canal de origen (source de Hostify). «Direct», «Website» y «Tu Sereno» se cuentan como un solo canal, Tu Sereno: son las reservas sin OTA. Comisión = (comisión del canal + tarifa de transacción) / facturación. Cancelación = canceladas / (confirmadas + canceladas) del canal. Años = primer y último año con reservas confirmadas."
        className="mb-6"
      >
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-left text-xs text-sereno-dark/50 border-b border-sereno-dark/10">
                <th className="py-2 font-medium">Canal</th>
                <th className="py-2 font-medium text-right">Reservas</th>
                <th className="py-2 font-medium text-right">Noches</th>
                <th className="py-2 font-medium text-right">Facturación</th>
                <th className="py-2 font-medium text-right">ADR</th>
                <th className="py-2 font-medium text-right">Estancia</th>
                <th className="py-2 font-medium text-right">Antelación</th>
                <th className="py-2 font-medium text-right">Huéspedes</th>
                <th className="py-2 font-medium text-right">Comisión</th>
                <th className="py-2 font-medium text-right">Cancelación</th>
                <th className="py-2 font-medium text-right">Años</th>
              </tr>
            </thead>
            <tbody>
              {data.canales.map((c) => (
                <tr key={c.canal} className="border-b border-sereno-dark/5 last:border-0">
                  <td className="py-2 font-semibold">{c.canal}</td>
                  <td className="py-2 text-right tabular-nums">
                    {fmtNum(c.reservas)} <span className="text-xs text-sereno-dark/40">({fmtPct(c.pct)})</span>
                  </td>
                  <td className="py-2 text-right tabular-nums">{fmtNum(c.noches)}</td>
                  <td className="py-2 text-right tabular-nums font-semibold">
                    {fmtMoney(c.facturacion)} <span className="text-xs text-sereno-dark/40 font-normal">({fmtPct(c.pctFacturacion)})</span>
                  </td>
                  <td className="py-2 text-right tabular-nums">{fmtMoney(c.adr)}</td>
                  <td className="py-2 text-right tabular-nums">{fmtNum(c.estanciaMedia, 1)} n.</td>
                  <td className="py-2 text-right tabular-nums">{fmtNum(c.antelacionMedia)} d.</td>
                  <td className="py-2 text-right tabular-nums">{fmtNum(c.huespedesMedia, 1)}</td>
                  <td className="py-2 text-right tabular-nums">{c.facturacion > 0 ? fmtPct(c.comisionPct, 1) : '—'}</td>
                  <td className="py-2 text-right tabular-nums">
                    {fmtPct(c.tasaCancelacion)} <span className="text-xs text-sereno-dark/40">({fmtNum(c.canceladas)})</span>
                  </td>
                  <td className="py-2 text-right tabular-nums text-sereno-dark/60">
                    {c.primerAnio == null ? '—' : c.primerAnio === c.ultimoAnio ? c.primerAnio : `${c.primerAnio}–${c.ultimoAnio}`}
                  </td>
                </tr>
              ))}
              {data.canales.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-4 text-center text-sereno-dark/40">Sin datos</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Tarjeta>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Tarjeta
          titulo="Reservas por canal y año"
          info={`Reservas confirmadas por año de entrada, apiladas por canal. Se muestran los ${MAX_CANALES_GRAFICA} canales con más reservas; el resto va en «Otros».`}
          className="lg:col-span-2"
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={grafica} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={tema.grid} />
              <XAxis dataKey="anio" tick={{ fontSize: 12, fill: tema.axis }} />
              <YAxis tick={{ fontSize: 12, fill: tema.axis }} />
              <Tooltip formatter={(v: any) => fmtNum(Number(v))} cursor={{ fill: tema.cursor }} contentStyle={tema.tooltip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {series.map((s, i) => (
                <Bar key={s} dataKey={s} stackId="canales" fill={tema.palette[i % tema.palette.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Tarjeta>

        <Tarjeta
          titulo="Estados en Hostify"
          info="Todos los documentos del container que pasan los filtros, por su estado en Hostify. Solo «Confirmada» cuenta en el resto de la pantalla; las consultas y caducadas son peticiones de Airbnb que no llegaron a reserva."
        >
          <div className="space-y-1.5">
            {data.estados.map((e) => (
              <div key={e.estado} className="flex items-center justify-between py-1 border-b border-sereno-dark/5 last:border-0 text-sm">
                <span className="inline-flex items-center gap-2">
                  <span className="text-sereno-dark/80">{ESTADO_LABEL[e.estado] || e.estado}</span>
                  <span className="text-[11px] text-sereno-dark/30 font-mono">{e.estado}</span>
                </span>
                <span className="tabular-nums">
                  <span className="font-semibold">{fmtNum(e.n)}</span>
                  <span className="ml-1.5 text-xs text-sereno-dark/40">{fmtPct(totalDocs > 0 ? (e.n / totalDocs) * 100 : 0)}</span>
                </span>
              </div>
            ))}
            {data.estados.length === 0 && <p className="text-sm text-sereno-dark/40">Sin datos</p>}
          </div>
        </Tarjeta>
      </div>
    </>
  );
}
